import {
  Dialog,
  DialogContent,
  IconButton,
  Button as MuiButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

export type OvertimeWeek = {
  week_start: string;
  current_hours: number;
  projected_hours: number;
  ot_hours: number;
  exceeds_40: boolean;
};

export type OvertimeProjection = {
  exceeds_40: boolean;
  weeks: OvertimeWeek[];
};

type Props = {
  isOpen: boolean;
  // Retained for API compatibility with existing callers (`useOvertimeGate`)
  // and to keep room for a future variant that surfaces worker-specific
  // detail. The current Figma design renders the same generic message
  // regardless, so the value goes unused in JSX.
  workerName?: string;
  projection?: OvertimeProjection;
  // Fail-closed variant: the projection couldn't be computed (request failed
  // or was blocked), so we can't confirm whether this assignment crosses the
  // 40h overtime limit. Renders alternate copy that asks the admin to make
  // an explicit call instead of silently proceeding.
  unavailable?: boolean;
  onProceed: () => void;
  onCancel: () => void;
};

const FONT = "'Manrope', sans-serif";
const THEME_GREEN = '#44A735';
const THEME_GREEN_HOVER = '#3A8E2D';
const BODY_TEXT = '#1C1C1C';

// GRAVY-10073: per the Figma redesign the modal is a single generic warning
// — no per-week breakdown, no worker name. Per-row hours and the OT-risk
// banner live on the staffing page now (see the wizard plan for that work).
export const OvertimeConfirmationModal: React.FC<Props> = ({
  isOpen,
  unavailable = false,
  onProceed,
  onCancel,
}) => {
  const title = unavailable ? 'Overtime Check Unavailable' : 'Overtime Charges';
  const body = unavailable
    ? "We couldn't verify this worker's weekly hours right now, so we can't confirm whether this assignment will exceed the 40-hour overtime limit. Would you like to continue?"
    : "Heads up! You've invited workers whose hours may exceed the 40-hour limit, which could result in overtime charges. Would you like to continue?";
  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '32px 40px',
          fontFamily: FONT,
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: FONT,
            fontWeight: 800,
            fontSize: '20px',
            lineHeight: '140%',
            letterSpacing: 0,
            color: BODY_TEXT,
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onCancel}
          data-testid="overtime-close"
          sx={{ marginTop: -1, marginRight: -1, color: BODY_TEXT }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent
        data-testid={
          unavailable
            ? 'overtime-unavailable-modal'
            : 'overtime-confirmation-modal'
        }
        sx={{
          padding: 0,
          marginTop: 4,
          marginBottom: 5,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT,
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '140%',
            letterSpacing: 0,
            color: BODY_TEXT,
          }}
        >
          {body}
        </Typography>
      </DialogContent>

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <MuiButton
          data-testid="overtime-cancel"
          variant="outlined"
          onClick={onCancel}
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '140%',
            letterSpacing: 0,
            textAlign: 'center',
            textTransform: 'none',
            borderColor: THEME_GREEN,
            color: THEME_GREEN,
            borderRadius: '8px',
            paddingX: 4,
            paddingY: 1.25,
            '&:hover': {
              borderColor: THEME_GREEN_HOVER,
              backgroundColor: 'rgba(68, 167, 53, 0.04)',
            },
          }}
        >
          Go back
        </MuiButton>
        <MuiButton
          data-testid="overtime-proceed"
          variant="contained"
          onClick={onProceed}
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '140%',
            letterSpacing: 0,
            textAlign: 'center',
            textTransform: 'none',
            backgroundColor: THEME_GREEN,
            color: '#FFFFFF',
            borderRadius: '8px',
            paddingX: 4,
            paddingY: 1.25,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: THEME_GREEN_HOVER,
              boxShadow: 'none',
            },
          }}
        >
          Yes, continue
        </MuiButton>
      </Stack>
    </Dialog>
  );
};

export default OvertimeConfirmationModal;
